// =============================================================================
// Pick'n'Drop Admin — Design tokens (single source of truth)
// -----------------------------------------------------------------------------
// The whole admin design system reads from this file. Colours are the
// logo-derived brand palette shared with the customer app
// (../pickndrop_public/constants/brand.js) so the two products read as one
// brand. Legacy `src/Constants/Colors.js` maps its keys onto these tokens, so
// old references keep working while new UI consumes tokens directly.
//
// Import in styled-components:  import { tokens } from "../../theme/tokens";
// =============================================================================

// --- Brand palette (sampled from logo.png: crimson→maroon gradient) ----------
const brand = {
    vermilion: "#d23c28", // brightest streak / top of gradient
    crimson: "#be1e28",   // dominant brand red — primary action/accent
    maroon: "#641e1e",    // deep body red
    ink: "#2a0e0e",       // near-black maroon (brand-panel base)
    inkDeep: "#170707",

    cream: "#fff8f5",     // warm off-white app background
    paper: "#ffffff",     // card / surface
    rose: "#fae7e3",      // light red tint (icon wash, hover)
    line: "#f0dcd7",      // hairline border on cream

    gold: "#e9a23b",      // warm accent (from the lighter streaks)

    text: "#2a1414",      // primary text on light
    textMute: "#8a6a65",  // secondary text on light
    onDark: "#ffffff",    // text on the brand panel
    onDarkMute: "#e9c9c4",
};

// --- Semantic status colours (pills, badges, alerts) -------------------------
// Each entry is { fg, bg, border } tuned for accessible contrast on the tint.
const status = {
    success: { fg: "#1b7a43", bg: "#e6f4ec", border: "#bfe3cd" },
    warn:    { fg: "#9a6a12", bg: "#fdf1dd", border: "#f2ddb0" },
    danger:  { fg: "#a3121c", bg: "#fbe6e6", border: "#f2c4c4" },
    info:    { fg: "#1160a3", bg: "#e6f0fb", border: "#c0dbf2" },
    neutral: { fg: "#5b4a47", bg: "#f3ece9", border: "#e4d5d0" },
    // brand-flavoured pill (e.g. highlighted / featured states)
    brand:   { fg: brand.crimson, bg: brand.rose, border: brand.line },
};

// --- Colour tokens -----------------------------------------------------------
const colors = {
    ...brand,

    // roles
    primary: brand.crimson,
    primaryHover: "#a91820",
    primaryActive: brand.maroon,
    accent: brand.gold,

    // surfaces
    bg: brand.cream,
    surface: brand.paper,
    surfaceMuted: "#fdf3ef", // slightly warmer than cream for zebra/hover
    surfaceSunken: "#f7ebe6",

    // text
    heading: brand.text,
    body: brand.text,
    muted: brand.textMute,
    faint: "#b39a95",

    // borders / dividers
    border: brand.line,
    borderStrong: "#e4cec8",
    divider: "#f2e4df",

    // focus ring
    focus: "rgba(190,30,40,0.35)",

    // status
    status,

    // convenience flat status colours (for text/icons)
    success: status.success.fg,
    warning: status.warn.fg,
    danger: status.danger.fg,
    info: status.info.fg,
};

// --- Spacing scale (px) ------------------------------------------------------
const spacing = {
    0: "0px",
    1: "4px",
    2: "8px",
    3: "12px",
    4: "16px",
    5: "24px",
    6: "32px",
    7: "48px",
    8: "64px",
};

// --- Radii -------------------------------------------------------------------
const radii = {
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "24px",
    pill: "999px",
    circle: "50%",
};

// --- Shadows (soft, crimson-tinted) ------------------------------------------
const shadows = {
    xs: "0 1px 2px rgba(42,14,14,0.06)",
    sm: "0 2px 8px rgba(42,14,14,0.06)",
    md: "0 8px 24px rgba(190,30,40,0.10)",
    lg: "0 18px 44px rgba(190,30,40,0.16)",
    xl: "0 28px 64px rgba(100,30,30,0.22)",
    focus: `0 0 0 3px ${colors.focus}`,
    insetLine: `inset 0 0 0 1px ${colors.border}`,
};

// --- Typography --------------------------------------------------------------
const typography = {
    fonts: {
        // Anton — the bold condensed display face used on the customer brand
        // panel/wordmark. Reserve for the auth brand panel & big numerals only.
        display: 'Anton, "Arial Narrow", Impact, sans-serif',
        // Manrope — the clean humanist body/UI face for the whole portal.
        body: 'Manrope, system-ui, -apple-system, "Segoe UI", sans-serif',
        mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
    },
    size: {
        xs: "11px",
        sm: "12px",
        base: "14px",
        md: "15px",
        lg: "17px",
        xl: "20px",
        "2xl": "24px",
        "3xl": "30px",
        "4xl": "38px",
        "5xl": "52px",
    },
    weight: {
        regular: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
        extrabold: 800,
    },
    leading: {
        tight: 1.15,
        snug: 1.3,
        normal: 1.5,
        relaxed: 1.7,
    },
    tracking: {
        tight: "-0.02em",
        normal: "0",
        wide: "0.04em",
        wider: "0.12em",
    },
};

// --- Layout / z-index / motion ----------------------------------------------
const zIndex = {
    base: 1,
    sticky: 100,
    dropdown: 1000,
    drawer: 1100,
    overlay: 1200,
    modal: 1300,
    toast: 1400,
    tooltip: 1500,
};

const breakpoints = {
    sm: "576px",
    md: "768px",
    lg: "992px",
    xl: "1200px",
};

const motion = {
    fast: "140ms",
    base: "220ms",
    slow: "320ms",
    ease: "cubic-bezier(.2,.75,.2,1)",
    easeOut: "cubic-bezier(0.16,1,0.3,1)",
};

export const tokens = {
    colors,
    brand,
    status,
    spacing,
    radii,
    shadows,
    typography,
    zIndex,
    breakpoints,
    motion,
};

// Small helper for media queries in styled-components.
export const mq = {
    sm: `@media (min-width: ${breakpoints.sm})`,
    md: `@media (min-width: ${breakpoints.md})`,
    lg: `@media (min-width: ${breakpoints.lg})`,
    xl: `@media (min-width: ${breakpoints.xl})`,
    down: {
        md: `@media (max-width: ${breakpoints.md})`,
        lg: `@media (max-width: ${breakpoints.lg})`,
    },
};

export default tokens;
